import { Inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { EIDSDK } from '../providers/eid-sdk.provider';
import { EidSessionService } from './eid-session.service';

@Injectable()
export class EidAuthService {
  private accessTokenSubject = new BehaviorSubject<string>('');
  accessToken$ = this.accessTokenSubject.asObservable();

  constructor(@Inject(EIDSDK) private readonly eid: any, private readonly eidSessionService: EidSessionService) {}

  public get accessToken(): Observable<string> {
    return from(this.eid.oidc.getAccessToken()).pipe(map((token: string | null) => token || ''));
  }

  public get idToken(): Observable<string> {
    return from(this.eid.oidc.getIdToken()).pipe(map((token: string | null) => token || ''));
  }

  public get isAuthenticated(): Observable<boolean> {
    return from(this.eid.oidc.isAuthenticated() as Promise<boolean>);
  }

  public async redirectToEidLogin(): Promise<void> {
    await this.eid.oidc.signIn();
  }

  public checkAuth(): Observable<any> {
    return from(this.eid.oidc.processSignInCallback());
  }

  public get events(): any {
    return this.eid.oidc.events;
  }

  public get checkSessionChanged(): Observable<boolean> {
    // The GP SDK handles session changes through events
    // This is a compatibility method - session changes are now handled via SDK events
    return new BehaviorSubject<boolean>(false).asObservable();
  }

  public setToken(token: string): void {
    this.accessTokenSubject.next(token);
  }

  public async logout(): Promise<void> {
    try {
      // Set flag BEFORE calling signOut to prevent responding to own broadcast
      this.eidSessionService.setManualLogoutFlag();

      await this.eid.oidc.signOut();

      // Clear any residual tokens to prevent incomplete token issue
      try {
        await this.eid.oidc.revokeTokens();
      } catch (revokeError) {
        // Revoke may fail if tokens already invalid, ignore
        console.warn('[EID Auth] Token revocation failed:', revokeError);
      }
    } catch (error) {
      console.error('[EID Auth] Error during logout:', error);
    }
  }

  public async refreshTokens(): Promise<void> {
    await this.eid.oidc.refreshTokens();
  }

  public async waitForSessionLoaded(): Promise<void> {
    const isAlreadyAuthenticated = await this.eid.oidc.isAuthenticated();
    if (isAlreadyAuthenticated) {
      return;
    }

    return new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, 10000);
      this.eid.oidc.events.addUserLoaded(() => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }
}
